import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link as RouterLink, Redirect } from 'react-router-dom';
import { observer } from 'mobx-react';

import Button from '@wui/input/button';
import Spacer from '@wui/layout/spacer';

import { digitalEstatePlanningService } from '@willing-shared/utils/env';
import { getFileSections } from 'ui/sections/sections';
import {
  fileSectionsWeight,
  notificationsWeight,
} from 'material-ui/components/planHealth/PlanHealthMeter';
import SectionSign from 'ui/sections/sign/SectionSign';
import SectionNotify from 'ui/sections/file/notify/SectionNotify';
import SectionsDisplay from 'material-ui/components/dashboard/SectionsDisplay';
import DashboardLayout from '@c/dashboard/DashboardLayout';
import { useDocumentTitle, useGlobalContext } from 'hooks';
import Urls from 'urls';

import Header from './Header';
import DigexDocumentList from 'material-ui/pages/PostPurchaseDashboard/DigexDocumentList';
import AnalogDocumentList from 'material-ui/pages/PostPurchaseDashboard/AnalogDocumentList';
import BundleBox from 'material-ui/pages/PostPurchaseDashboard/BundleBox';
import Grid from '@material-ui/core/Grid';
import { API } from 'API';
import { base64ToBlob } from 'utils';
import { ExperienceType } from '!/utils/enums';
import { DocumentBundle, DocumentType } from 'utils/enums';

const BUNDLE_LAUNCH_DATE = new Date(2025, 6, 23);

const goToMembers = () => {
  window.location = `${window.WillingConfig.membersAppLink}/services`;
};

const PostPurchaseDashboard = ({ title }) => {
  useDocumentTitle(title);
  const { clientData, serverData, clientDataHolder } = useGlobalContext();
  const [hasTrust, setHasTrust] = useState(false);
  const [documentsUrl, setDocumentsUrl] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notarizedAt, setNotarizedAt] = useState(null);
  const intervalRef = useRef(null);
  const { day, month, year } = clientData.notarizationDate;
  let signedAt = notarizedAt;
  if (!signedAt && day && month && year) {
    signedAt = new Date(year, month - 1, day);
  }

  // This is a bit ugly, but we need to check that the document has been processed and uploaded to S3 before we allow
  // the user to attempt to download them.
  const updateDocumentsUrl = useRef(async () => {
    try {
      const {
        data: { urls },
      } = await API.getNotarizationEvidenceUrls();
      if (urls.downloadPdfUrl) {
        setDocumentsUrl(urls.downloadPdfUrl);
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  });

  useEffect(() => {
    if (!serverData.hasNotarizedOnline) {
      return () => null;
    }
    intervalRef.current = setInterval(() => updateDocumentsUrl.current(), 2000);
    updateDocumentsUrl.current();
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [serverData.hasNotarizedOnline]);

  useEffect(() => {
    API.getDocumentsInfo(DocumentBundle.HOMEOWNERS).then(({ sections }) => {
      const allDocuments = sections.flatMap(section => section.documents);
      setHasTrust(allDocuments.some(doc => doc.document_type === DocumentType.TRUST));
    });
  }, []);

  // Fetch the last completed execution session
  useEffect(() => {
    if (!serverData.hasNotarizedOnline) {
      return;
    }

    API.getNotarizationCompletedSessionInfo().then(({ data }) => {
      if (!data.notarizedAt) {
        return;
      }
      setNotarizedAt(new Date(data.notarizedAt * 1000));
    });
  }, [serverData.hasNotarizedOnline, setNotarizedAt]);

  const fileSections = useMemo(() => {
    const sections =
      clientData.covidEmployer || serverData.experienceType === ExperienceType.FREE_LOOK
        ? [SectionSign]
        : [SectionSign].concat(getFileSections(clientData, serverData));
    const fileSectionsPercent = fileSectionsWeight * 100;
    const notificationsPercent = notificationsWeight * 100;

    const percentageGiven = Math.floor(fileSectionsPercent / sections.length);
    const isFractional = fileSectionsPercent % sections.length !== 0;

    const percentageText = isNotify => {
      const prefix = isFractional ? '~' : '';
      const percentage = isNotify ? notificationsPercent + percentageGiven : percentageGiven;

      if (clientData.covidEmployer || serverData.fromPortunus) {
        return '';
      }
      return `Increase your plan health by ${prefix}${percentage}%`;
    };

    return sections.map(
      s =>
        class extends s {
          static description = percentageText(s === SectionNotify);
        },
    );
  }, [clientData, serverData]);

  // Send anyone who has not completed their plan to plan dashboard
  if (!serverData.hasNotarizedOnline && !clientData.isPlanValid()) {
    return <Redirect to={clientDataHolder.dashboardUrl} />;
  }
  const showExitButton =
    serverData.fromPortunus &&
    ![ExperienceType.UPWISE, ExperienceType.FREE_LOOK].includes(serverData.experienceType);

  const buttonSection = (
    <>
      <Spacer v={32} />
      <Grid container direction="row" justify="space-between" alignItems="center" spacing={2}>
        {showExitButton && (
          <Grid item>
            <Button variant="contained" color="primary" onClick={goToMembers}>
              Exit to main menu
            </Button>
          </Grid>
        )}
        {serverData.hasNotarizedOnline && (
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!documentsUrl}
                  href={documentsUrl}
                >
                  {documentsUrl ? 'Download All' : 'Loading...'}
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" color="primary" component={RouterLink} to={Urls.storage}>
                  View All
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );

  // If they signed before we switched to self-service deeds through Bundle
  // they get the old page layout
  if (signedAt && signedAt < BUNDLE_LAUNCH_DATE) {
    return (
      <DashboardLayout showPlanHealth={false}>
        <Header />

        <Spacer v={32} smDown />
        <Spacer v={48} mdUp />

        {serverData.hasNotarizedOnline ? (
          <DigexDocumentList />
        ) : (
          <SectionsDisplay sections={fileSections} />
        )}

        {buttonSection}

        {serverData.experienceType === ExperienceType.UPWISE && (
          <>
            <Spacer v={32} />
            <Button variant="contained" color="primary" href="#close">
              Return to Upwise
            </Button>
          </>
        )}
      </DashboardLayout>
    );
  }

  // Otherwise, render the same UI for everyone
  const goToDocuments = async download => {
    if (loading) {
      return;
    }
    setLoading(true);
    const response = await API.getDocumentsPdf({ digitalEstatePlanningService });

    if (download) {
      const downloadLink = document.createElement('a');
      downloadLink.href = `data:application/pdf;base64,${response.data}`;
      downloadLink.download = clientData.documentsFileName;
      downloadLink.click();
    } else {
      const blob = base64ToBlob(response.data);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl);
    }

    setLoading(false);
  };

  const downloadButtons = serverData.hasNotarizedOnline ? (
    <Grid container direction="row" justify="space-between" alignItems="center" spacing={2}>
      {serverData.hasNotarizedOnline && (
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                disabled={!documentsUrl}
                href={documentsUrl}
              >
                {documentsUrl ? 'Download All' : 'Loading...'}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" color="primary" component={RouterLink} to={Urls.storage}>
                View All
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  ) : (
    <Grid container direction="row" alignItems="center" spacing={2}>
      <Grid item>
        <Button
          variant="outlined"
          color="primary"
          noMinWidth
          onClick={() => goToDocuments(true)}
          disabled={loading}
        >
          {loading ? 'Loading' : 'Download All'}
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="outlined"
          color="primary"
          noMinWidth
          onClick={() => goToDocuments()}
          disabled={loading}
        >
          {loading ? 'Loading' : 'View All'}
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <DashboardLayout showPlanHealth={false}>
      <Header showCompleted={true} />

      <Spacer v={16} smDown />
      <Spacer v={48} mdUp />

      <Spacer v={8} />

      {downloadButtons}
      <Spacer v={16} />

      <AnalogDocumentList />

      {hasTrust && <BundleBox />}

      {serverData.experienceType === ExperienceType.UPWISE && (
        <>
          <Spacer v={32} />
          <Button variant="contained" color="primary" href="#close">
            Return to Upwise
          </Button>
        </>
      )}
    </DashboardLayout>
  );
};

PostPurchaseDashboard.propTypes = {
  title: PropTypes.string,
};
export default observer(PostPurchaseDashboard);
